import React, { useState } from "react";
import { Box, Paper, TextField, Button, Stack, MenuItem, Dialog, DialogTitle, DialogContent, Typography } from "@mui/material";
import { Trash2, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { webhookService } from "../../api/services/automationService";
import { PageHeader, ConfirmDialog, StatusChip } from "../../components/Common";
import DataTable from "../../components/DataTable";

const EVENT_TYPES = ["ticket.created", "ticket.updated", "ticket.comment_added", "sla.breach"];

export default function WebhooksPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({ url: "", event_type: EVENT_TYPES[0], secret: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null);

  const { data: webhooks, isLoading } = useQuery({ queryKey: ["webhooks"], queryFn: webhookService.list });

  const createMutation = useMutation({
    mutationFn: () => webhookService.subscribe(form),
    onSuccess: () => {
      setForm({ url: "", event_type: EVENT_TYPES[0], secret: "" });
      enqueueSnackbar("Webhook subscribed", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => webhookService.delete(id),
    onSuccess: () => {
      enqueueSnackbar("Webhook removed", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: "event_type", label: "Event" },
    { key: "url", label: "Target URL" },
    { key: "is_active", label: "Status", render: (w) => <StatusChip value={w.is_active ? "active" : "closed"} label={w.is_active ? "Active" : "Inactive"} /> },
    {
      key: "actions", label: "", width: 140,
      render: (w) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={(e) => { e.stopPropagation(); setDeliveriesFor(w); }}>Deliveries</Button>
          <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(w); }}><Trash2 size={16} /></Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="Webhooks" subtitle="Deliveries are HMAC-signed (X-Ats-Signature header) when a secret is set" />

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack spacing={2}>
          <TextField select label="Event" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
            {EVENT_TYPES.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </TextField>
          <TextField label="Target URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} fullWidth />
          <TextField label="Secret (optional, for HMAC signing)" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} fullWidth />
          <Box>
            <Button variant="contained" startIcon={<Plus size={16} />} disabled={!form.url || createMutation.isPending} onClick={() => createMutation.mutate()}>
              Subscribe
            </Button>
          </Box>
        </Stack>
      </Paper>

      <DataTable columns={columns} rows={webhooks} loading={isLoading} emptyMessage="No webhooks configured" />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Remove webhook?"
        message="This deletes the subscription and its delivery log."
        confirmLabel="Remove" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />

      <DeliveriesDialog webhook={deliveriesFor} onClose={() => setDeliveriesFor(null)} />
    </Box>
  );
}

function DeliveriesDialog({ webhook, onClose }) {
  const { data: deliveries } = useQuery({
    queryKey: ["webhook-deliveries", webhook?.id],
    queryFn: () => webhookService.deliveries(webhook.id),
    enabled: Boolean(webhook),
  });

  return (
    <Dialog open={Boolean(webhook)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Deliveries — {webhook?.event_type}</DialogTitle>
      <DialogContent>
        {(!deliveries || deliveries.length === 0) && (
          <Typography variant="body2" color="text.secondary">No deliveries logged yet.</Typography>
        )}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {(deliveries || []).map((d) => (
            <Paper key={d.id} variant="outlined" sx={{ p: 1.5, display: "flex", justifyContent: "space-between" }}>
              <StatusChip value={d.status} />
              <Typography variant="caption" color="text.secondary">
                {d.response_code ?? "-"} · {new Date(d.created_at).toLocaleString()}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
